import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import { Box, Container, Link, Typography } from '@mui/material'
import { useLocale, useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useRef } from 'react'

import { Offer } from '@/src/types/offers'
import BannerCard from './BannerCard'
import DesktopArrow from './DesktopArrow'

const AUTO_PLAY_INTERVAL = 3000
const CARD_WIDTH = 380 // Approximate card width + gap

interface DesktopHomepageCarouselProps {
    locale: string
    offers: Offer[]
}

export const DesktopHomepageCarousel: React.FC<DesktopHomepageCarouselProps> = ({ locale, offers }) => {
    const t = useTranslations('homePage.headings')
    const currentLocale = useLocale()
    const isRTL = currentLocale === 'ar'
    const scrollContainerRef = useRef<HTMLDivElement>(null)
    const autoPlayRef = useRef<NodeJS.Timeout | null>(null)
    const isHoveredRef = useRef(false)

    const scrollToNext = useCallback(() => {
        const container = scrollContainerRef.current
        if (!container) return

        const maxScroll = container.scrollWidth - container.clientWidth
        const currentScroll = container.scrollLeft
        const nextScroll = currentScroll + CARD_WIDTH

        // If we're near the end, scroll back to start
        if (nextScroll >= maxScroll) {
            container.scrollTo({ left: 0, behavior: 'smooth' })
        } else {
            container.scrollTo({ left: nextScroll, behavior: 'smooth' })
        }
    }, [])

    const scrollToPrev = useCallback(() => {
        const container = scrollContainerRef.current
        if (!container) return

        const currentScroll = container.scrollLeft
        const prevScroll = currentScroll - CARD_WIDTH

        // If we're at the start, scroll to end
        if (prevScroll <= 0) {
            container.scrollTo({ left: container.scrollWidth, behavior: 'smooth' })
        } else {
            container.scrollTo({ left: prevScroll, behavior: 'smooth' })
        }
    }, [])

    // Auto-play
    useEffect(() => {
        const startAutoPlay = () => {
            if (autoPlayRef.current) clearInterval(autoPlayRef.current)
            autoPlayRef.current = setInterval(() => {
                if (!isHoveredRef.current) {
                    scrollToNext()
                }
            }, AUTO_PLAY_INTERVAL)
        }

        startAutoPlay()
        return () => {
            if (autoPlayRef.current) clearInterval(autoPlayRef.current)
        }
    }, [scrollToNext])

    const handleMouseEnter = () => {
        isHoveredRef.current = true
    }

    const handleMouseLeave = () => {
        isHoveredRef.current = false
    }

    const handleOfferClick = useCallback(
        (slug: string | undefined) => {
            if (slug) {
                window.open(`/${locale}/offers/${slug}`, '_blank')
            }
        },
        [locale]
    )

    if (!offers?.length) {
        return null
    }

    const leftArrowHandler = isRTL ? scrollToNext : scrollToPrev
    const rightArrowHandler = isRTL ? scrollToPrev : scrollToNext
    const leftArrowIcon = isRTL ? <ChevronRight fontSize="large" /> : <ChevronLeft fontSize="large" />
    const rightArrowIcon = isRTL ? <ChevronLeft fontSize="large" /> : <ChevronRight fontSize="large" />

    return (
        <Container maxWidth="xl" sx={{ px: { xs: 1, sm: 2 }, mb: 1 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: 1,
                    mb: 3,
                    px: { lg: 1 }
                }}
            >
                <Typography
                    component="h2"
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        fontSize: '1.25rem'
                    }}
                >
                    {t('topDealsForYou')}
                </Typography>
                <Link
                    href={`/${locale}/offers`}
                    sx={{
                        color: 'primary.main',
                        textDecoration: 'none',
                        fontSize: '1rem',
                        fontWeight: 600,
                        '&:hover': {
                            textDecoration: 'underline'
                        }
                    }}
                >
                    {t('viewAll')}
                </Link>
            </Box>

            <Box
                sx={{
                    position: 'relative',
                    mx: 'auto',
                    maxWidth: { xs: '100%', md: '1200px' },
                    direction: isRTL ? 'rtl' : 'ltr'
                }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                <DesktopArrow direction="left" onClick={leftArrowHandler}>
                    {leftArrowIcon}
                </DesktopArrow>

                <DesktopArrow direction="right" onClick={rightArrowHandler}>
                    {rightArrowIcon}
                </DesktopArrow>

                {/* Carousel Container */}
                <Box
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before, &::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            width: '60px',
                            zIndex: 10,
                            pointerEvents: 'none'
                        },
                        '&::before': {
                            left: 0,
                            background: 'linear-gradient(to right, rgba(255, 255, 255, 0.95), transparent)'
                        },
                        '&::after': {
                            right: 0,
                            background: 'linear-gradient(to left, rgba(255, 255, 255, 0.95), transparent)'
                        }
                    }}
                >
                    <Box
                        ref={scrollContainerRef}
                        sx={{
                            display: 'flex',
                            gap: 3,
                            overflowX: 'auto',
                            scrollSnapType: 'x mandatory',
                            scrollBehavior: 'smooth',
                            '&::-webkit-scrollbar': { display: 'none' },
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none'
                        }}
                    >
                        {offers.map((offer, index) => (
                            <Box
                                key={`desktop-${offer.id}-${index}`}
                                sx={{
                                    flex: '0 0 calc(33.333% - 16px)',
                                    minWidth: 0,
                                    scrollSnapAlign: 'start'
                                }}
                            >
                                <BannerCard offer={offer} onOfferClick={handleOfferClick} priority={index < 3} />
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
        </Container>
    )
}

export default DesktopHomepageCarousel
export type { DesktopHomepageCarouselProps }
